import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, CalendarCheck, ClipboardList, RefreshCw } from "lucide-react";
import "./landing/landing.css";

import { API } from "@/apiBase";
const TKEY = "coach_admin_token";

const ink = "#0a1e38", inkSoft = "#5a7088", clay = "#1e7be5", paper = "#eef4fc", line = "rgba(10,30,56,.13)";
const shell = { fontFamily: '"Hanken Grotesk",system-ui,sans-serif', color: ink, minHeight: "100vh", padding: "0 20px" };
const cardStyle = { background: "#fff", border: `1px solid ${line}`, borderRadius: 20, padding: 28, boxShadow: "0 20px 44px -28px rgba(10,30,56,.35)" };
const serif = { fontFamily: '"Fraunces",Georgia,serif', fontWeight: 500 };

// Shared by the dashboard tables and by DataRequestPanel below. Hoisted out of
// the Admin component when the second consumer appeared; they never read state.
const th = { textAlign: "left", padding: "10px 12px", fontSize: ".68rem", letterSpacing: ".1em", textTransform: "uppercase", color: inkSoft, borderBottom: `1px solid ${line}` };
const td = { padding: "12px", fontSize: ".9rem", borderBottom: `1px solid rgba(10,30,56,.06)`, verticalAlign: "top" };
const badge = (txt, ok) => <span style={{ fontSize: ".72rem", fontWeight: 600, padding: "3px 9px", borderRadius: 100, background: ok ? "rgba(94,224,192,.25)" : "rgba(255,107,92,.18)", color: ok ? "#0b6b53" : "#c0392b" }}>{txt}</span>;

function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TKEY) || ""}` }; }

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(TKEY, data.token); onDone();
    } catch (e2) { setErr(e2.message || "Login failed"); } finally { setLoading(false); }
  };

  return (
    <div style={{ ...shell, display: "grid", placeItems: "center" }}>
      <div style={{ ...cardStyle, width: "100%", maxWidth: 400, marginTop: 80 }}>
        <div style={{ fontFamily: '"Fraunces"', fontWeight: 600, fontSize: "1.4rem", letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 4 }}>Coach <span style={{ color: clay }}>OJ</span></div>
        <h1 style={{ ...serif, fontSize: "1.6rem", margin: "6px 0 22px" }}>Admin sign in</h1>
        <form onSubmit={submit}>
          <label style={{ fontSize: ".74rem", fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: inkSoft }}>Email</label>
          <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            style={{ width: "100%", padding: "11px 14px", margin: "6px 0 16px", border: `1.5px solid ${line}`, borderRadius: 12, font: "inherit" }} />
          <label style={{ fontSize: ".74rem", fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: inkSoft }}>Password</label>
          <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            style={{ width: "100%", padding: "11px 14px", margin: "6px 0 8px", border: `1.5px solid ${line}`, borderRadius: 12, font: "inherit" }} />
          {err && <div data-testid="admin-login-error" style={{ color: "#c0392b", fontSize: ".9rem", margin: "8px 0" }}>{err}</div>}
          <button data-testid="admin-login-btn" type="submit" disabled={loading}
            style={{ width: "100%", marginTop: 14, padding: "12px", borderRadius: 100, border: "none", background: ink, color: "#fff", fontWeight: 600, cursor: "pointer", font: "inherit", opacity: loading ? 0.6 : 1 }}>
            {loading ? "…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

// A plaque, in the sense of the framed one hung above a shop counter.
//
// Two lines: the basmala, then four of the divine names in the vocative -
// al-Fattah (the Opener), al-Alim (the All-Knowing), al-Razzaq (the Provider),
// al-Karim (the Generous). Set in Aref Ruqaa, a ruqaa-style face that reads as
// written by hand rather than typed, which is the whole point of a plaque.
//
// The font is loaded here rather than in index.html so the public site never
// pays for a font only the owner ever sees.
const BASMALA = "\u0628\u0650\u0633\u0652\u0645\u0650 \u0627\u0644\u0644\u0651\u064E\u0647\u0650 \u0627\u0644\u0631\u0651\u064E\u062D\u0652\u0645\u064E\u0670\u0646\u0650 \u0627\u0644\u0631\u0651\u064E\u062D\u0650\u064A\u0645\u0650";
const NAMES = "\u064A\u064E\u0627 \u0641\u064E\u062A\u0651\u064E\u0627\u062D\u064F \u064A\u064E\u0627 \u0639\u064E\u0644\u0650\u064A\u0645\u064F \u064A\u064E\u0627 \u0631\u064E\u0632\u0651\u064E\u0627\u0642\u064F \u064A\u064E\u0627 \u0643\u064E\u0631\u0650\u064A\u0645\u064F";

const GOLD = "#b08d3f", GOLD_SOFT = "rgba(176,141,63,.32)";

function Plaque() {
  useEffect(() => {
    const HREF = "https://fonts.googleapis.com/css2?family=Aref+Ruqaa:wght@400;700&display=swap";
    if (document.querySelector(`link[href="${HREF}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = HREF;
    document.head.appendChild(l);
  }, []);

  // Arabic script needs its own font stack and its own direction. Tajawal is
  // already loaded site-wide and stands in until Aref Ruqaa arrives, so the
  // text is never briefly rendered in a Latin fallback that cannot draw it.
  const arabic = {
    fontFamily: '"Aref Ruqaa","Tajawal",serif',
    direction: "rtl",
    unicodeBidi: "isolate",
  };

  return (
    <div
      dir="rtl"
      lang="ar"
      aria-label="Bismillah al-Rahman al-Rahim, ya Fattah ya Alim ya Razzaq ya Karim"
      style={{
        margin: "0 auto 24px",
        maxWidth: 760,
        textAlign: "center",
        padding: "26px 30px 22px",
        borderRadius: 18,
        background: "linear-gradient(180deg,#fffefb 0%,#fdfaf3 100%)",
        border: `1px solid ${GOLD_SOFT}`,
        boxShadow: "0 18px 40px -30px rgba(10,30,56,.45)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* a thin gold rule inset from the frame, the way a real plaque is bordered */}
      <div style={{
        position: "absolute", inset: 7, borderRadius: 13,
        border: `1px solid ${GOLD_SOFT}`, pointerEvents: "none",
      }} />

      <div style={{
        ...arabic, fontSize: "clamp(1.6rem, 4.2vw, 2.5rem)", fontWeight: 700,
        color: ink, lineHeight: 1.9, letterSpacing: 0,
      }}>
        {BASMALA}
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 12, margin: "12px auto 10px", maxWidth: 320, color: GOLD,
      }}>
        <span style={{ flex: 1, height: 1, background: GOLD_SOFT }} />
        <span style={{ fontSize: ".8rem", lineHeight: 1 }}>&#10087;</span>
        <span style={{ flex: 1, height: 1, background: GOLD_SOFT }} />
      </div>

      <div style={{
        ...arabic, fontSize: "clamp(1rem, 2.4vw, 1.32rem)", fontWeight: 400,
        color: GOLD, lineHeight: 2, opacity: .95,
      }}>
        {NAMES}
      </div>
    </div>
  );
}

// "up 40% on last month" is only meaningful when there was a last month to
// compare against. With no prior figure this says so rather than dividing by
// zero and reporting an infinite rise.
const deltaLabel = (now, prev) => {
  if (!prev) return now ? "first month with takings" : "nothing yet";
  const pct = Math.round(((now - prev) / prev) * 100);
  if (pct === 0) return "level with last month";
  return `${pct > 0 ? "up" : "down"} ${Math.abs(pct)}% on last month`;
};

const money = (cents, currency) => {
  const value = ((cents || 0) / 100).toFixed(2).replace(/\.00$/, "");
  return (currency || "eur") === "eur" ? `${value} \u20AC` : `$${value}`;
};

// Issue a private price to one person. Tiers are a share off the normal price,
// so the same three buttons make sense for every plan; the box below overrides
// them entirely when you want an exact figure.
/* Answering a request from somebody about their own data.
 *
 * Deliberately absent from this component: any field that accepts an address
 * to send to. The export and the drafted reply are addressed from the record
 * itself. Somebody can write in pretending to be a client, and a screen with
 * a "send to" box is how that turns into handing one person another person's
 * file. The back end refuses a destination too; this is the same rule stated
 * in the place where the temptation actually appears.
 *
 * The order of the panel is the order of the obligation: see what is held,
 * hand it over, and only then erase. The Erase section stays shut until an
 * export has been downloaded, and the server refuses without the receipt
 * regardless of what this screen allows.
 */
/* Booking somebody in by hand.
 *
 * The price has two doors and neither is the six Stripe prices: type whatever
 * was agreed, or pick one of the codes already issued. Retyping a discount
 * from memory is how the wrong figure gets into the books, and hunting for a
 * code to express a price already agreed is busywork.
 *
 * Marking it paid asks first. Not because the click is dangerous, but because
 * of what it makes true: a paid booking is an accounting record from that
 * moment, kept for ten years, and it cannot be deleted afterwards without
 * exporting it first. Better to know that going in.
 */
function NewBookingForm({ offers, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [code, setCode] = useState("");
  const [paid, setPaid] = useState(false);
  const [method, setMethod] = useState("cash");
  const [makeLink, setMakeLink] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [made, setMade] = useState(null);

  // Only codes that could actually be used. A revoked or spent one is refused
  // by the server anyway; offering it here would just be a click that fails.
  const usable = (offers || []).filter((o) => !o.revoked && !o.used_at && !o.expired);
  const picked = usable.find((o) => o.code === code);

  const reset = () => {
    setName(""); setEmail(""); setDate(""); setSlot(""); setNote("");
    setAmount(""); setCode(""); setPaid(false); setMethod("cash");
    setMakeLink(false); setErr("");
  };

  const submit = async () => {
    setBusy(true); setErr(""); setMade(null);
    try {
      const cents = Math.round(parseFloat(String(amount).replace(",", ".") || "0") * 100);
      const r = await fetch(`${API}/admin/bookings`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          name: name.trim(), email: email.trim(),
          date: date.trim(), slot: slot.trim(), admin_note: note.trim(),
          amount: Number.isFinite(cents) ? cents : 0,
          offer_code: code, paid,
          payment_method: paid ? method : "",
          make_link: makeLink,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || "That could not be saved."); return; }
      setMade(d); reset(); setOpen(false);
      if (onCreated) await onCreated();
    } catch { setErr("Could not reach the server."); } finally { setBusy(false); }
  };

  const box = { font: "inherit", fontSize: ".9rem", padding: "9px 12px", borderRadius: 9,
                border: `1px solid ${line}` };
  const label = { fontSize: ".78rem", color: inkSoft, marginBottom: 4, display: "block" };

  if (!open) {
    return (
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => { setOpen(true); setMade(null); }}
                style={{ font: "inherit", fontSize: ".88rem", fontWeight: 600, padding: "9px 18px",
                         borderRadius: 100, border: `1.5px solid ${ink}`, background: ink,
                         color: "#fff", cursor: "pointer" }}>
          Book someone in
        </button>
        {made && (
          <div style={{ ...cardStyle, marginTop: 10, padding: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Saved.</div>
            <div style={{ color: inkSoft, fontSize: ".86rem" }}>
              {made.paid ? `Recorded as paid — ${money(made.amount, made.currency)}.` : "Recorded, nothing paid."}
              {made.calendly !== "skipped" ? ` Calendly: ${made.calendly}.` : ""}
            </div>
            {made.booking_url ? (
              <div style={{ marginTop: 8 }}>
                <div style={label}>Single-use link — send this to them</div>
                <input readOnly value={made.booking_url} onFocus={(e) => e.target.select()}
                       style={{ ...box, width: "min(520px, 100%)", fontFamily: "ui-monospace, monospace",
                                fontSize: ".78rem" }} />
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      <h3 style={{ ...serif, margin: "0 0 4px", fontSize: "1.05rem" }}>Book someone in</h3>
      <p style={{ color: inkSoft, fontSize: ".85rem", margin: "0 0 14px" }}>
        Only the name is needed. Everything else is what you happen to know.
      </p>

      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
        <div><span style={label}>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...box, width: "100%" }} /></div>
        <div><span style={label}>Email (optional)</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...box, width: "100%" }} /></div>
        <div><span style={label}>Date (optional)</span>
          <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="2026-09-14"
                 style={{ ...box, width: "100%" }} /></div>
        <div><span style={label}>Time (optional)</span>
          <input value={slot} onChange={(e) => setSlot(e.target.value)} placeholder="14:00"
                 style={{ ...box, width: "100%" }} /></div>
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${line}` }}>
        <div style={{ fontWeight: 600, fontSize: ".9rem", marginBottom: 8 }}>Price</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
          <div><span style={label}>Amount agreed</span>
            <input value={amount} onChange={(e) => { setAmount(e.target.value); setCode(""); }}
                   disabled={Boolean(code)} placeholder="e.g. 45"
                   style={{ ...box, width: "100%", opacity: code ? .5 : 1 }} /></div>
          <div><span style={label}>…or one of your codes</span>
            <select value={code} onChange={(e) => { setCode(e.target.value); setAmount(""); }}
                    style={{ ...box, width: "100%" }}>
              <option value="">—</option>
              {usable.map((o) => (
                <option key={o.id} value={o.code}>
                  {o.code} · {money(o.amount, o.currency)} · {o.plan_label}
                </option>
              ))}
            </select></div>
        </div>
        {picked ? (
          <p style={{ color: inkSoft, fontSize: ".8rem", margin: "8px 0 0" }}>
            {money(picked.amount, picked.currency)} — the code's price is used, and the code is
            spent when you mark this paid.
          </p>
        ) : null}
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${line}` }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: ".9rem" }}>
          <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
          Already paid me
        </label>
        {paid && (
          <div style={{ marginTop: 10 }}>
            <span style={label}>How it arrived</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["cash", "transfer", "other"].map((m) => (
                <button key={m} onClick={() => setMethod(m)}
                        style={{ font: "inherit", fontSize: ".84rem", fontWeight: 600, padding: "7px 15px",
                                 borderRadius: 100, cursor: "pointer",
                                 border: `1px solid ${method === m ? ink : line}`,
                                 background: method === m ? ink : "#fff",
                                 color: method === m ? "#fff" : ink }}>{m}</button>
              ))}
            </div>
            {/* Said before the click, not discovered after it. */}
            <p style={{ color: "#8a4b00", fontSize: ".82rem", margin: "10px 0 0" }}>
              This becomes an accounting record, kept for ten years. It cannot be marked as test
              data afterwards, and removing it will need the record exported first.
            </p>
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${line}` }}>
        <span style={label}>Your note (optional)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)}
               placeholder="agreed on the phone, prefers mornings"
               style={{ ...box, width: "100%" }} />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: ".88rem", marginTop: 10 }}>
          <input type="checkbox" checked={makeLink} onChange={(e) => setMakeLink(e.target.checked)} />
          Also make a single-use Calendly link to send them
        </label>
      </div>

      {err && <p style={{ color: "#c0392b", fontSize: ".88rem", margin: "12px 0 0" }}>{err}</p>}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={submit} disabled={busy || !name.trim()}
                style={{ font: "inherit", fontSize: ".88rem", fontWeight: 600, padding: "9px 18px",
                         borderRadius: 100, border: `1.5px solid ${ink}`,
                         background: name.trim() ? ink : "#fff", color: name.trim() ? "#fff" : inkSoft,
                         cursor: name.trim() ? "pointer" : "not-allowed", opacity: busy ? .6 : 1 }}>
          {busy ? "Saving…" : "Save the booking"}
        </button>
        <button onClick={() => { reset(); setOpen(false); }}
                style={{ font: "inherit", fontSize: ".88rem", fontWeight: 600, padding: "9px 18px",
                         borderRadius: 100, border: `1.5px solid ${line}`, background: "#fff",
                         color: ink, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}

function DataRequestPanel({ requests, onLogged }) {
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [receipt, setReceipt] = useState("");
  const [exported, setExported] = useState("");     // the address the receipt belongs to
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const [reply, setReply] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const reset = () => {
    setPreview(null); setReceipt(""); setExported("");
    setConfirmEmail(""); setReason(""); setReply(null); setMsg(""); setErr("");
  };

  const lookUp = async () => {
    reset(); setBusy("lookup");
    try {
      const r = await fetch(`${API}/admin/data-request/preview?email=${encodeURIComponent(email.trim())}`,
                            { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || "That lookup was refused."); return; }
      setPreview(d);
    } catch { setErr("Could not reach the server."); } finally { setBusy(""); }
  };

  /* The export is downloaded here, in the browser. Nothing about a person is
   * written to the server's own disk - on Render that is wiped by the next
   * deploy, which is the "kept record" that quietly evaporates. */
  const exportNow = async () => {
    setBusy("export"); setErr(""); setMsg("");
    try {
      const r = await fetch(`${API}/admin/data-request/export?email=${encodeURIComponent(email.trim())}`,
                            { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || "The export was refused."); return; }
      const blob = new Blob([d.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || "coach-oj-donnees.html";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setReceipt(d.receipt); setExported(d.email);
      setMsg(`Saved. ${d.found} record${d.found === 1 ? "" : "s"} for ${d.name || d.email}.`);
      if (onLogged) onLogged();
    } catch { setErr("Could not reach the server."); } finally { setBusy(""); }
  };

  const getReply = async (lang, erased) => {
    setBusy("reply");
    try {
      const r = await fetch(
        `${API}/admin/data-request/reply?email=${encodeURIComponent(email.trim())}&lang=${lang}&erased=${erased ? "true" : "false"}`,
        { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (r.ok) setReply(d);
    } finally { setBusy(""); }
  };

  const erase = async () => {
    setBusy("erase"); setErr(""); setMsg("");
    try {
      const r = await fetch(`${API}/admin/data-request/erase`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ email: email.trim(), receipt, note: reason.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || "The erasure was refused."); return; }
      setMsg(`Done. ${d.plan.total_anonymise} anonymised, ${d.plan.total_delete} deleted.`);
      setPreview(null); setReceipt(""); setConfirmEmail("");
      await getReply("fr", true);
      if (onLogged) onLogged();
    } catch { setErr("Could not reach the server."); } finally { setBusy(""); }
  };

  const box = { font: "inherit", fontSize: ".9rem", padding: "9px 12px", borderRadius: 9, border: `1px solid ${line}` };
  const btn = (on) => ({ font: "inherit", fontSize: ".86rem", fontWeight: 600, padding: "9px 18px", borderRadius: 100,
                         border: `1.5px solid ${on ? ink : line}`, background: on ? ink : "#fff",
                         color: on ? "#fff" : ink, cursor: on ? "pointer" : "not-allowed", opacity: on ? 1 : .5 });

  const readyToErase = Boolean(
    receipt && preview &&
    exported.toLowerCase() === email.trim().toLowerCase() &&
    confirmEmail.trim().toLowerCase() === email.trim().toLowerCase() &&
    reason.trim().length >= 3
  );

  return (
    <>
      {/* What is still owed. Forgetting is likelier than answering badly. */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ ...serif, margin: "0 0 10px", fontSize: "1.05rem" }}>Requests received</h3>
        {(!requests || requests.items.length === 0) && (
          <p style={{ color: inkSoft, fontSize: ".9rem", margin: 0 }}>Nothing recorded yet.</p>
        )}
        {requests && requests.items.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Who</th><th style={th}>Asked</th><th style={th}>State</th></tr></thead>
            <tbody>
              {requests.items.slice(0, 20).map((q) => (
                <tr key={q.id}>
                  <td style={td}>{q.email}<div style={{ color: inkSoft, fontSize: ".78rem" }}>{q.kind}</div></td>
                  <td style={td}>{new Date(q.received_at).toLocaleDateString()}</td>
                  <td style={td}>
                    {q.answered_at
                      ? badge("answered", true)
                      : badge(q.late ? `${Math.abs(q.days_left)} days late` : `${q.days_left} days left`, !q.late)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ ...cardStyle }}>
        <h3 style={{ ...serif, margin: "0 0 4px", fontSize: "1.05rem" }}>Answer a request</h3>
        <p style={{ color: inkSoft, fontSize: ".86rem", margin: "0 0 14px" }}>
          The complete email address, exactly as they wrote it. Partial addresses are refused -
          half an address matching three people would be a breach.
        </p>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          <input value={email} onChange={(e) => { setEmail(e.target.value); reset(); }}
                 placeholder="someone@example.com" style={{ ...box, minWidth: 280, flex: 1 }} />
          <button onClick={lookUp} disabled={!email.trim() || busy === "lookup"} style={btn(Boolean(email.trim()))}>
            {busy === "lookup" ? "Looking…" : "Look up"}
          </button>
        </div>

        {err && <p style={{ color: "#c0392b", fontSize: ".88rem", margin: "0 0 12px" }}>{err}</p>}
        {msg && <p style={{ color: "#0b6b53", fontSize: ".88rem", margin: "0 0 12px" }}>{msg}</p>}

        {preview && (
          <>
            <div style={{ background: paper, border: `1px solid ${line}`, borderRadius: 12, padding: "13px 15px", marginBottom: 14 }}>
              {preview.found === 0 ? (
                <p style={{ margin: 0, fontSize: ".9rem" }}>
                  Nothing held at that address. You can still send the export - it says so, which
                  is a better answer than silence.
                </p>
              ) : (
                <>
                  <p style={{ margin: "0 0 6px", fontSize: ".9rem" }}>
                    <b>{preview.found}</b> record{preview.found === 1 ? "" : "s"}
                    {preview.name ? <> for <b>{preview.name}</b></> : null}
                  </p>
                  <p style={{ margin: 0, color: inkSoft, fontSize: ".84rem" }}>
                    {Object.entries(preview.counts).filter(([, n]) => n > 0)
                      .map(([t, n]) => `${t.replace(/_/g, " ")}: ${n}`).join(" · ")}
                  </p>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
              <button onClick={exportNow} disabled={busy === "export"} style={btn(true)}>
                {busy === "export" ? "Preparing…" : "Export and save"}
              </button>
              <button onClick={() => getReply("fr", false)} style={btn(true)}>Draft the reply</button>
            </div>
            <p style={{ color: inkSoft, fontSize: ".8rem", margin: "0 0 18px" }}>
              The file downloads to this computer. It is addressed to {email.trim() || "the record"} and
              says so on the first page - send it only there.
            </p>
          </>
        )}

        {reply && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
              {[["fr", "Français"], ["en", "English"], ["ar", "العربية"]].map(([l, label]) => (
                <button key={l} onClick={() => getReply(l, Boolean(msg && msg.startsWith("Done")))}
                        style={{ ...btn(true), fontSize: ".8rem", padding: "6px 14px",
                                 background: reply.lang === l ? ink : "#fff", color: reply.lang === l ? "#fff" : ink }}>
                  {label}
                </button>
              ))}
              <span style={{ color: inkSoft, fontSize: ".8rem", marginInlineStart: "auto" }}>
                To: {reply.to}
              </span>
            </div>
            <textarea readOnly value={reply.text} rows={14}
                      style={{ ...box, width: "100%", fontFamily: "ui-monospace, monospace", fontSize: ".8rem",
                               direction: reply.lang === "ar" ? "rtl" : "ltr" }} />
          </div>
        )}

        {preview && preview.found > 0 && (
          <div style={{ border: `1px solid rgba(255,107,92,.4)`, borderRadius: 14, padding: "15px 17px" }}>
            <h4 style={{ margin: "0 0 4px", fontSize: ".95rem" }}>Erase</h4>
            <p style={{ color: inkSoft, fontSize: ".84rem", margin: "0 0 10px" }}>
              Where money changed hands the amount, the date and the Stripe reference stay and the
              name and address are removed - French law needs the transaction, not the person.
              Everything else is deleted outright.
            </p>
            <p style={{ fontSize: ".88rem", margin: "0 0 12px" }}>
              This will <b>anonymise {preview.plan.total_anonymise}</b> and{" "}
              <b>delete {preview.plan.total_delete}</b> record
              {preview.plan.total_delete === 1 ? "" : "s"}.
            </p>

            {!receipt && (
              <p style={{ color: "#8a4b00", fontSize: ".85rem", margin: "0 0 10px" }}>
                Export first. The record has to leave before the identity goes - the server refuses
                otherwise, so this is not only a rule on this screen.
              </p>
            )}

            <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
              <input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                     disabled={!receipt} placeholder="Type their email address to confirm" style={box} />
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                     disabled={!receipt} placeholder="Why - kept in the log, not in the record" style={box} />
            </div>

            <button onClick={erase} disabled={!readyToErase || busy === "erase"} style={btn(readyToErase)}>
              {busy === "erase" ? "Erasing…" : "Erase these records"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function OfferForm({ catalog, onCreated }) {
  const [plan, setPlan] = useState("coach_single");
  const [currency, setCurrency] = useState("eur");
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [made, setMade] = useState(null);

  if (!catalog) return null;

  const full = (catalog.plans.find((p) => p.key === plan) || {})[currency] || 0;

  const submit = async (body) => {
    setBusy(true); setError(""); setMade(null);
    try {
      const res = await fetch(`${API}/admin/offers`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ plan, currency, email: email.trim() || null, note, expires_days: Number(days) || 30, ...body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not create that offer.");
      setMade(data); setCustom(""); setEmail(""); setNote("");
      onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const field = { font: "inherit", fontSize: ".92rem", padding: "9px 11px", borderRadius: 9, border: `1px solid ${line}`, width: "100%" };
  const label = { display: "block", fontSize: ".78rem", fontWeight: 600, color: inkSoft, marginBottom: 5 };

  return (
    <div style={{ ...cardStyle, marginBottom: 18 }}>
      <div style={{ ...serif, fontSize: "1.2rem", marginBottom: 4 }}>Create a private offer</div>
      <div style={{ color: inkSoft, fontSize: ".88rem", marginBottom: 18 }}>
        One code, one person, one use. The price lives on the server \u2014 nobody can change it from their browser.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 14 }}>
        <div>
          <span style={label}>Plan</span>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} style={field}>
            {catalog.plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <span style={label}>Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={field}>
            <option value="eur">EUR</option><option value="usd">USD</option>
          </select>
        </div>
        <div>
          <span style={label}>Expires after</span>
          <select value={days} onChange={(e) => setDays(e.target.value)} style={field}>
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <span style={label}>Normally {money(full, currency)} \u2014 pick a tier</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {catalog.tiers.map((t) => (
            <button key={t.key} disabled={busy} onClick={() => submit({ tier: t.key })}
              style={{ font: "inherit", fontWeight: 600, fontSize: ".9rem", padding: "9px 15px", borderRadius: 100,
                       border: `1.5px solid ${line}`, background: "#fff", color: ink, cursor: busy ? "wait" : "pointer" }}>
              {money(t.amounts[plan][currency], currency)}
              <span style={{ color: inkSoft, fontWeight: 500 }}> \u00b7 {t.percent_off}% off</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, alignItems: "end" }}>
        <div>
          <span style={label}>Or an exact amount</span>
          <input value={custom} onChange={(e) => setCustom(e.target.value)} inputMode="decimal"
            placeholder={`min ${money(catalog.min_amount, currency)}`} style={field} />
        </div>
        <div>
          <span style={label}>Lock to an email (optional)</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="them@example.com" style={field} />
        </div>
        <div>
          <span style={label}>Your note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="why, or who" style={field} />
        </div>
        <button disabled={busy || !custom}
          onClick={() => submit({ amount: Math.round(parseFloat(custom.replace(",", ".")) * 100) })}
          style={{ font: "inherit", fontWeight: 600, padding: "10px 18px", borderRadius: 100, border: "none",
                   background: custom ? clay : line, color: "#fff", cursor: custom ? "pointer" : "not-allowed" }}>
          Create at that price
        </button>
      </div>

      {error && <div style={{ marginTop: 14, color: "#c0392b", fontWeight: 600, fontSize: ".9rem" }}>{error}</div>}
      {made && (
        <div style={{ marginTop: 16, padding: "13px 16px", borderRadius: 11, background: "rgba(94,224,192,.18)", color: "#0b6b53" }}>
          <div style={{ fontWeight: 700, letterSpacing: ".08em", fontFamily: "ui-monospace, monospace", fontSize: "1.05rem" }}>{made.code}</div>
          <div style={{ fontSize: ".88rem", marginTop: 4 }}>
            {made.plan_label} at {money(made.amount, made.currency)} \u00b7 valid until {new Date(made.expires_at).toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
}

/* What actually arrived. Omar's own view, deliberately kept out of the books.
 *
 * The panel above this one is the business as it gets declared: what clients
 * were charged. That is the figure the micro-BNC ceiling and the VAT threshold
 * are measured against, and Stripe's fees are an expense rather than a
 * reduction of turnover - under micro-BNC they are already inside the 34%
 * abattement, so taking them off the revenue as well would be counting them
 * twice and declaring the wrong number.
 *
 * This panel answers the other question. Of everything charged, how much of it
 * actually reached the bank, and where did the rest go. Four figures, side by
 * side, none of them folded into another - because the last time a fee and a
 * withheld tax were indistinguishable, 17% of every sale went out of the door
 * unnoticed.
 *
 * It is shut by default and loads only when opened, so it stays a separate
 * view rather than another number on the dashboard that could be misread as
 * turnover.
 */
function NetPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("year");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetch(`${API}/admin/money/net`, { headers: authHeaders() });
      if (!r.ok) throw new Error("The server refused the request.");
      setData(await r.json());
    } catch (e) {
      setErr(e.message || "Could not load these figures.");
    }
  }, []);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const catchUp = async () => {
    setBusy(true); setNote(""); setErr("");
    try {
      const r = await fetch(`${API}/admin/money/settlements/catch-up`, {
        method: "POST", headers: authHeaders(),
      });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || "Stripe could not be asked.");
      setNote(out.recorded
        ? `Filled in ${out.recorded} of ${out.looked_at}. ${out.still_missing} still unknown.`
        : `Nothing could be filled in. ${out.still_missing} still unknown.`);
      setData(null);
      await load();
    } catch (e) {
      setErr(e.message || "Stripe could not be asked.");
    } finally {
      setBusy(false); }
  };

  const P = data ? data.periods[period] : null;
  const PERIODS = [["month", "This month"], ["year", "This year"], ["all", "Everything"]];

  const Fig = ({ label, value, sub, alarm }) => (
    <div>
      <div style={{ color: inkSoft, fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ ...serif, fontSize: "1.35rem", lineHeight: 1.2, marginTop: 3, color: alarm ? "#c0392b" : ink }}>{value}</div>
      {sub ? <div style={{ color: alarm ? "#c0392b" : inkSoft, fontSize: ".75rem", marginTop: 2 }}>{sub}</div> : null}
    </div>
  );

  return (
    <div style={{ ...cardStyle, marginBottom: 20, padding: "16px 22px" }}>
      <button onClick={() => setOpen(!open)}
        style={{ font: "inherit", background: "none", border: 0, padding: 0, cursor: "pointer", color: ink, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ ...serif, fontSize: "1.05rem" }}>What actually arrived</span>
        <span style={{ color: inkSoft, fontSize: ".8rem" }}>{open ? "hide" : "your figures, not the books"}</span>
      </button>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {PERIODS.map(([k, lbl]) => (
              <button key={k} onClick={() => setPeriod(k)}
                style={{ font: "inherit", fontSize: ".8rem", fontWeight: 600, padding: "5px 12px", borderRadius: 100, cursor: "pointer",
                         border: `1px solid ${period === k ? clay : line}`,
                         background: period === k ? paper : "#fff", color: period === k ? clay : inkSoft }}>
                {lbl}
              </button>
            ))}
          </div>

          {err ? <div style={{ color: "#c0392b", fontSize: ".85rem" }}>{err}</div> : null}
          {!data && !err ? <div style={{ color: inkSoft, fontSize: ".85rem" }}>Reading…</div> : null}

          {P && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 18 }}>
                <Fig label="Charged" value={money(P.charged_known_cents, "eur")}
                     sub="what clients paid" />
                <Fig label="Card fees" value={money(P.fees_cents, "eur")} sub="Stripe's cut" />
                <Fig label="Tax withheld" value={money(P.withheld_tax_cents, "eur")}
                     alarm={P.withheld_tax_cents > 0}
                     sub={P.withheld_tax_cents > 0 ? "should be zero — chase this" : "none, as it should be"} />
                <Fig label="Arrived" value={money(P.received_cents, "eur")} sub="in the bank" />
              </div>

              {/* Coverage, always. A net total that quietly skipped the
                  payments it knows nothing about would read low and be
                  believed. */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${line}`, fontSize: ".82rem", color: inkSoft }}>
                {P.complete
                  ? `Covers all ${P.paid_count} paid booking${P.paid_count === 1 ? "" : "s"} in this period.`
                  : `Covers ${P.known_count} of ${P.paid_count} paid bookings. ${P.missing_count} have no payout recorded, so they are left out of every figure above — including "Charged".`}
                {" "}
                {P.charged_cents !== P.charged_known_cents
                  ? `The books show ${money(P.charged_cents, "eur")} charged for this period; that is the figure to declare.`
                  : "This matches the charged total in the books."}
              </div>

              {!P.complete && (
                <div style={{ marginTop: 10 }}>
                  <button onClick={catchUp} disabled={busy}
                    style={{ font: "inherit", fontSize: ".8rem", fontWeight: 600, padding: "6px 13px", borderRadius: 100,
                             border: `1px solid ${line}`, background: "#fff", color: ink, cursor: busy ? "wait" : "pointer" }}>
                    {busy ? "Asking Stripe…" : "Ask Stripe about the missing ones"}
                  </button>
                  <span style={{ color: inkSoft, fontSize: ".78rem", marginLeft: 10 }}>
                    Payments taken before this was recorded. Cash and transfers are never missing.
                  </span>
                </div>
              )}
              {note ? <div style={{ marginTop: 8, fontSize: ".82rem", color: inkSoft }}>{note}</div> : null}

              {data.withheld_tax_total_cents > 0 && (
                <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 12, background: "rgba(255,107,92,.1)", color: "#c0392b", fontSize: ".84rem" }}>
                  {money(data.withheld_tax_total_cents, "eur")} has been withheld as tax across every payment.
                  Coach OJ trades under the franchise en base de TVA and owes none of it.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function Dashboard({ onLogout }) {
  const [accepting, setAccepting] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState({ bookings: 0, paid: 0, waitlist: 0, applications: 0, offers_open: 0 });
  const [applications, setApplications] = useState([]);
  const [offers, setOffers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [trafficDays, setTrafficDays] = useState(30);
  const [view, setView] = useState("upcoming");
  const [sort, setSort] = useState("date");
  const [direction, setDirection] = useState("asc");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");        // debounced copy actually sent
  const [total, setTotal] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [binMsg, setBinMsg] = useState("");
  const [cancelling, setCancelling] = useState(null);   // booking id with the panel open
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRefund, setCancelRefund] = useState(false);
  const [rowMsg, setRowMsg] = useState({});             // booking id -> what happened
  const [dataRequests, setDataRequests] = useState(null);

  // Every failure here used to leave the page sitting at its starting values —
  // three zeros and an empty table — which is indistinguishable from a quiet
  // day. An unreachable backend must never look like "no bookings".
  const load = useCallback(async () => {
    const opts = { headers: authHeaders() };
    try {
      const params = new URLSearchParams({ view, sort, direction, limit: "200" });
      if (search.trim()) params.set("q", search.trim());
      const [s, b, w, st, ap, of, tr, me, an, dr] = await Promise.all([
        fetch(`${API}/admin/settings`, opts), fetch(`${API}/admin/bookings?${params}`, opts),
        fetch(`${API}/admin/waitlist`, opts), fetch(`${API}/admin/stats`, opts),
        fetch(`${API}/admin/applications`, opts), fetch(`${API}/admin/offers`, opts),
        fetch(`${API}/admin/offers/tiers`, opts), fetch(`${API}/admin/metrics`, opts),
        fetch(`${API}/admin/analytics?days=${trafficDays}`, opts),
        fetch(`${API}/admin/data-requests`, opts),
      ]);
      if (s.status === 401) { onLogout(); return; }
      if (![s, b, w, st, ap, of, tr, me, an, dr].every((r) => r.ok)) throw new Error("The server refused one of the requests.");
      setAccepting((await s.json()).accepting_clients);
      const page = await b.json();
      // The endpoint used to return a bare array and now returns a page object.
      // Tolerate both so a half-deployed backend degrades instead of blanking.
      setBookings(Array.isArray(page) ? page : page.items || []);
      setTotal(Array.isArray(page) ? page.length : page.total || 0);
      setWaitlist(await w.json()); setStats(await st.json());
      setApplications(await ap.json()); setOffers(await of.json()); setCatalog(await tr.json());
      setMetrics(await me.json());
      setTraffic(await an.json());
      setDataRequests(await dr.json());
      setLoadError("");
    } catch (e) {
      setLoadError("Could not reach the server, so the figures below are not real. " + (e.message || ""));
    }
  }, [onLogout, view, sort, direction, search, trafficDays]);

  useEffect(() => { load(); }, [load]);

  // Debounce so a search does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  const toggle = async () => {
    setSaving(true);
    const next = !accepting;
    const res = await fetch(`${API}/admin/settings`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ accepting_clients: next }) });
    if (res.ok) setAccepting(next);
    setSaving(false);
  };

  const SortTh = ({ id, children }) => {
    const on = sort === id;
    return (
      <th style={{ ...th, cursor: "pointer", userSelect: "none" }}
          onClick={() => { if (on) setDirection(direction === "asc" ? "desc" : "asc"); else { setSort(id); setDirection("asc"); } }}>
        {children}{on ? (direction === "asc" ? " \u2191" : " \u2193") : ""}
      </th>
    );
  };

  const Breakdown = ({ title, rows, empty }) => {
    const top = (rows || []).slice(0, 6);
    const max = top.reduce((m, r) => Math.max(m, r.n), 0) || 1;
    return (
      <div style={{ ...cardStyle, padding: 20 }}>
        <div style={{ color: inkSoft, fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10 }}>{title}</div>
        {top.length === 0 ? (
          <div style={{ color: inkSoft, fontSize: ".82rem", lineHeight: 1.5 }}>{empty || "Nothing yet."}</div>
        ) : top.map((r) => (
          <div key={r.k} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".84rem", marginBottom: 3 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{r.k}</span>
              <span style={{ color: inkSoft }}>{r.n}</span>
            </div>
            <div style={{ height: 5, borderRadius: 100, background: paper, overflow: "hidden" }}>
              <div style={{ width: `${(r.n / max) * 100}%`, height: "100%", background: clay, opacity: .6 }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const Metric = ({ label, value, sub }) => (
    <div>
      <div style={{ color: inkSoft, fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ ...serif, fontSize: "1.45rem", lineHeight: 1.2, marginTop: 3 }}>{value}</div>
      {sub ? <div style={{ color: inkSoft, fontSize: ".76rem", marginTop: 2 }}>{sub}</div> : null}
    </div>
  );

  const Ceiling = ({ label, used, cap }) => {
    const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    const hot = pct >= 80;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".8rem", marginBottom: 5 }}>
          <span style={{ color: inkSoft }}>{label}</span>
          <span style={{ fontWeight: 600, color: hot ? "#c0392b" : ink }}>
            {money(used, "eur")} / {money(cap, "eur")}
          </span>
        </div>
        <div style={{ height: 7, borderRadius: 100, background: paper, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: hot ? "#c0392b" : clay }} />
        </div>
        {hot ? <div style={{ color: "#c0392b", fontSize: ".75rem", marginTop: 4 }}>
          {pct}% \u2014 worth talking to your accountant before you cross it.
        </div> : null}
      </div>
    );
  };

  const StatCard = ({ icon, label, value }) => (
    <div style={{ ...cardStyle, padding: 20, display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: paper, display: "grid", placeItems: "center", color: clay }}>{icon}</div>
      <div><div style={{ ...serif, fontSize: "1.7rem", lineHeight: 1 }}>{value}</div><div style={{ color: inkSoft, fontSize: ".82rem" }}>{label}</div></div>
    </div>
  );

  const smallBtn = {
    font: "inherit", fontSize: ".8rem", fontWeight: 600, padding: "5px 11px",
    borderRadius: 100, border: `1px solid ${line}`, background: "#fff", color: ink, cursor: "pointer",
  };

  const post = async (url, body) => {
    const r = await fetch(url, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    return { ok: r.ok, data };
  };

  const markNoShow = async (b) => {
    const on = !!b.no_show_at;
    const { ok, data } = await post(`${API}/admin/bookings/${b.id}/no-show`, { undo: on, note: "" });
    setRowMsg((m) => ({ ...m, [b.id]: ok ? (on ? "No-show cleared." : "Marked as a no-show.") : (data.detail || "That did not work.") }));
    if (ok) await load();
  };

  const toggleArchive = async (b) => {
    const on = !!b.archived_at;
    const { ok, data } = await post(`${API}/admin/bookings/${b.id}/archive?undo=${on}`);
    setRowMsg((m) => ({ ...m, [b.id]: ok ? (on ? "Back in the list." : "Archived \u2014 still searchable.") : (data.detail || "That did not work.") }));
    if (ok) await load();
  };

  const toggleTest = async (b) => {
    const { ok, data } = await post(`${API}/admin/bookings/${b.id}/test-flag`, { is_test: !b.is_test });
    setRowMsg((m) => ({ ...m, [b.id]: ok ? (b.is_test ? "No longer test data." : "Marked as test data.") : (data.detail || "That did not work.") }));
    if (ok) await load();
  };

  // One delete button. Nothing here destroys anything: a booking goes to the
  // bin, out of the working books entirely, and only a purge removes it.
  //
  // Where money changed hands the server refuses without an export first, so
  // the flow below downloads the record and passes the receipt back. The
  // refusal lives in the API, not in this component - a screen gets rebuilt,
  // and a rule that lived only in one takes its leave with it.
  const del = async (b, reason = "") => {
    setRowMsg((m) => ({ ...m, [b.id]: "" }));
    let receipt = "";

    if (b.protected) {
      // Export first, and download it here rather than on the server, whose
      // disk Render wipes on the next deploy.
      const e = await fetch(`${API}/admin/bookings/${b.id}/deletion-export`, { headers: authHeaders() });
      const doc = await e.json().catch(() => ({}));
      if (!e.ok) {
        setRowMsg((m) => ({ ...m, [b.id]: doc.detail || "Could not prepare the record." }));
        return;
      }
      const blob = new Blob([doc.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename || "booking.html";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      receipt = doc.receipt;
    }

    const q = new URLSearchParams();
    if (receipt) q.set("receipt", receipt);
    if (reason.trim()) q.set("reason", reason.trim());
    const r = await fetch(`${API}/admin/bookings/${b.id}?${q}`, { method: "DELETE", headers: authHeaders() });
    const data = await r.json().catch(() => ({}));
    setConfirmDelete(null); setDeleteReason("");
    if (r.ok) { await load(); } else {
      setRowMsg((m) => ({ ...m, [b.id]: data.detail || "The server refused to delete that." }));
    }
  };

  const restore = async (b) => {
    const r = await fetch(`${API}/admin/bookings/${b.id}/restore`, { method: "POST", headers: authHeaders() });
    if (r.ok) await load();
  };

  // The irreversible one. Only reachable from the bin, and for a booking where
  // money changed hands this is the ONLY way out - the 90-day timer will not
  // take it.
  const purge = async (b) => {
    const r = await fetch(`${API}/admin/bookings/${b.id}/purge`, { method: "DELETE", headers: authHeaders() });
    const data = await r.json().catch(() => ({}));
    setConfirmPurge(null);
    if (r.ok) { await load(); } else {
      setRowMsg((m) => ({ ...m, [b.id]: data.detail || "The server refused that." }));
    }
  };

  const emptyBin = async () => {
    const r = await fetch(`${API}/admin/trash/empty`, { method: "POST", headers: authHeaders() });
    const data = await r.json().catch(() => ({}));
    if (r.ok) { setBinMsg(`${data.purged} removed.`); await load(); }
  };

  const saveNote = async (b, text) => {
    const r = await fetch(`${API}/admin/bookings/${b.id}/note`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ admin_note: text }),
    });
    if (r.ok) { setEditingNote(null); await load(); }
  };

  // Cancelling touches three systems that can each fail on their own: our
  // database, Calendly, and Stripe. The server reports on each separately and
  // we show all of it - a single "done" would be a lie the first time one of
  // them is down.
  const cancelBooking = async (id) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/bookings/${id}/cancel`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ reason: cancelReason, refund: cancelRefund, cancel_in_calendly: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setRowMsg((m) => ({ ...m, [id]: data.detail || "The server refused that." }));
      } else {
        setRowMsg((m) => ({ ...m, [id]: `Cancelled. Calendly: ${data.calendly}. Refund: ${data.refund}.` }));
        setCancelling(null); setCancelReason(""); setCancelRefund(false);
        await load();
      }
    } catch (e) {
      setRowMsg((m) => ({ ...m, [id]: "Could not reach the server, so nothing was cancelled." }));
    }
    setSaving(false);
  };

  // Calendly will not let us move someone else's booking, which is correct -
  // the client owns their diary. We fetch their personal reschedule link and
  // copy it so he can send it to them.
  const getRescheduleLink = async (id) => {
    setRowMsg((m) => ({ ...m, [id]: "Fetching the link\u2026" }));
    try {
      const r = await fetch(`${API}/admin/bookings/${id}/reschedule-link`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (data.link) {
        try { await navigator.clipboard.writeText(data.link); } catch (e) {}
        setRowMsg((m) => ({ ...m, [id]: `Reschedule link copied \u2014 send it to ${data.email}` }));
      } else {
        setRowMsg((m) => ({ ...m, [id]: data.reason || "No reschedule link available." }));
      }
    } catch (e) {
      setRowMsg((m) => ({ ...m, [id]: "Could not reach the server." }));
    }
  };

  const setApplicationStatus = async (id, status) => {
    await fetch(`${API}/admin/applications/${id}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ status }),
    });
    load();
  };

  const revokeOffer = async (id) => {
    await fetch(`${API}/admin/offers/${id}/revoke`, { method: "POST", headers: authHeaders() });
    load();
  };

  // The link drops the code straight into the booking sheet, so the person you
  // send it to does not have to type anything.
  const copyLink = (o) => {
    const url = `https://coachoj.com/?code=${o.code}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    window.prompt("Send this to them:", url);
  };


  return (
    <div style={{ ...shell, maxWidth: 1080, margin: "0 auto", paddingTop: 30, paddingBottom: 60 }}>
      <Plaque />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 26 }}>
        <div>
          <div style={{ fontFamily: '"Fraunces"', fontWeight: 600, letterSpacing: ".14em", textTransform: "uppercase" }}>Coach <span style={{ color: clay }}>OJ</span> · Admin</div>
          <h1 style={{ ...serif, fontSize: "1.9rem", marginTop: 4 }}>Dashboard</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button data-testid="admin-refresh-btn" onClick={load} style={{ display: "inline-flex", gap: 8, alignItems: "center", padding: "9px 16px", borderRadius: 100, border: `1.5px solid ${ink}`, background: "transparent", color: ink, fontWeight: 600, cursor: "pointer", font: "inherit" }}><RefreshCw size={16} /> Refresh</button>
          <button data-testid="admin-logout-btn" onClick={onLogout} style={{ display: "inline-flex", gap: 8, alignItems: "center", padding: "9px 16px", borderRadius: 100, border: "none", background: ink, color: "#fff", fontWeight: 600, cursor: "pointer", font: "inherit" }}><LogOut size={16} /> Log out</button>
        </div>
      </div>

      {loadError && (
        <div data-testid="admin-load-error" style={{ ...cardStyle, marginBottom: 20, padding: "14px 18px",
          background: "rgba(255,107,92,.12)", border: "1px solid rgba(255,107,92,.5)", color: "#a3281c", fontWeight: 600 }}>
          {loadError}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 16 }}>
        <StatCard icon={<CalendarCheck size={20} />} label="Total bookings" value={stats.bookings} />
        <StatCard icon={<ClipboardList size={20} />} label="Paid bookings" value={stats.paid} />
        <StatCard icon={<Users size={20} />} label="On waitlist" value={stats.waitlist} />
      </div>

      {metrics && (
        <div style={{ ...cardStyle, marginBottom: 20, padding: "18px 22px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 18 }}>
            <Metric label="This month"
                    value={money(metrics.money.month_cents, "eur")}
                    sub={deltaLabel(metrics.money.month_cents, metrics.money.prev_month_cents)} />
            <Metric label="Year to date" value={money(metrics.money.year_cents, "eur")}
                    sub={`${metrics.money.paid_count} paid`} />
            <Metric label="Average session"
                    value={metrics.money.paid_count ? money(metrics.money.average_cents, "eur") : "\u2014"} />
            <Metric label="Refunded"
                    value={metrics.money.refunded_cents ? money(metrics.money.refunded_cents, "eur") : "\u2014"} />
            <Metric label="Upcoming" value={metrics.sessions.upcoming}
                    sub={`${metrics.sessions.completed} done`} />
            <Metric label="No-show rate"
                    value={metrics.sessions.no_show_rate === null ? "\u2014" : `${metrics.sessions.no_show_rate}%`}
                    sub={metrics.sessions.no_shows ? `${metrics.sessions.no_shows} missed` : "none yet"} />
            <Metric label="Repeat clients"
                    value={metrics.clients.repeat_rate === null ? "\u2014" : `${metrics.clients.repeat_rate}%`}
                    sub={`${metrics.clients.returning} of ${metrics.clients.total}`} />
            <Metric label="Codes used"
                    value={`${metrics.offers.used}/${metrics.offers.issued}`} sub="issued" />
          </div>

          {/* The two ceilings. Deliberately blank until real figures are
              supplied - an invented French tax threshold displayed with
              confidence is one that would actually get acted on. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${line}` }}>
            {metrics.thresholds.configured ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 18 }}>
                <Ceiling label="VAT threshold" used={metrics.money.year_cents}
                         cap={metrics.thresholds.vat_threshold_cents} />
                <Ceiling label="Micro-BNC ceiling" used={metrics.money.year_cents}
                         cap={metrics.thresholds.micro_ceiling_cents} />
              </div>
            ) : (
              <div style={{ color: inkSoft, fontSize: ".85rem" }}>
                <b style={{ color: ink }}>Your two ceilings are not set.</b>{" "}
                The VAT threshold and the micro-BNC turnover ceiling are the two numbers
                that can force a mid-year change to every price on your site. They are left
                blank on purpose rather than guessed \u2014 confirm the current figures with
                URSSAF or your accountant and they will show here as a progress bar.
              </div>
            )}
          </div>
        </div>
      )}

      <NetPanel />

      <div style={{ ...cardStyle, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ ...serif, fontSize: "1.2rem" }}>Accepting new clients</div>
          <div style={{ color: inkSoft, fontSize: ".9rem", marginTop: 4 }}>
            When off, every "Book" button becomes "Join the waitlist" and new bookings are blocked server-side.
          </div>
          <div style={{ marginTop: 8 }} data-testid="capacity-status">{accepting ? badge("OPEN — taking bookings", true) : badge("FULL — waitlist only", false)}</div>
        </div>
        <button data-testid="capacity-toggle" onClick={toggle} disabled={saving} aria-pressed={!accepting}
          style={{ position: "relative", width: 72, height: 38, borderRadius: 100, border: "none", cursor: "pointer", background: accepting ? "#5ee0c0" : "#ff6b5c", transition: "background .25s", opacity: saving ? 0.6 : 1 }}>
          <span style={{ position: "absolute", top: 4, left: accepting ? 4 : 38, width: 30, height: 30, borderRadius: "50%", background: "#fff", transition: "left .25s", boxShadow: "0 2px 6px rgba(0,0,0,.25)" }} />
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[["bookings", "Bookings"], ["waitlist", "Waitlist"],
          ["applications", `Requests${stats.applications ? ` (${stats.applications})` : ""}`],
          ["offers", "Private offers"], ["traffic", "Traffic"],
          ["data", `Data requests${dataRequests && dataRequests.open ? ` (${dataRequests.open})` : ""}`]].map(([id, label]) => (
          <button key={id} data-testid={`${id}-tab`} onClick={() => setTab(id)}
            style={{ padding: "8px 18px", borderRadius: 100, border: `1.5px solid ${tab === id ? ink : line}`, background: tab === id ? ink : "#fff", color: tab === id ? "#fff" : ink, fontWeight: 600, cursor: "pointer", font: "inherit" }}>{label}</button>
        ))}
      </div>

      {tab === "bookings" && (
        <>
        <NewBookingForm offers={offers} onCreated={load} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["upcoming", "Upcoming"], ["past", "Past"], ["cancelled", "Cancelled"],
              ["archived", "Archive"], ["test", "Test data"], ["all", "Everything"],
              ["deleted", "Deleted / other"]].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)}
                style={{ padding: "6px 13px", borderRadius: 100, font: "inherit", fontSize: ".82rem", cursor: "pointer",
                         fontWeight: 600, border: `1px solid ${view === id ? clay : line}`,
                         background: view === id ? clay : "#fff", color: view === id ? "#fff" : ink }}>{label}</button>
            ))}
          </div>
          <input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search name, email, plan or code\u2026"
                 style={{ font: "inherit", fontSize: ".86rem", padding: "7px 12px", borderRadius: 9,
                          border: `1px solid ${line}`, minWidth: 240, marginInlineStart: "auto" }} />
        </div>

        <div style={{ color: inkSoft, fontSize: ".8rem", marginBottom: 8 }}>
          {total} {total === 1 ? "booking" : "bookings"}
          {view === "archived" ? " \u00b7 sessions more than three months past land here on their own" : ""}
          {view === "test" ? " \u00b7 marked as test data, and out of every revenue figure" : ""}
          {view === "deleted" ? " \u00b7 out of every list and every total. Unpaid ones clear themselves after 90 days; paid ones wait for you." : ""}
          {view === "deleted" && (
            <button style={{ ...smallBtn, marginInlineStart: 10 }} onClick={emptyBin}>
              Empty what is due
            </button>
          )}
          {view === "deleted" && binMsg ? <span style={{ marginInlineStart: 8 }}>{binMsg}</span> : null}
        </div>

        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <table data-testid="bookings-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <SortTh id="name">Name</SortTh>
              <th style={th}>Email</th>
              <SortTh id="amount">Plan</SortTh>
              <SortTh id="date">Date / slot</SortTh>
              <SortTh id="payment">Payment</SortTh>
              <th style={th}>Actions</th>
            </tr></thead>
            <tbody>
              {bookings.length === 0 && <tr><td style={td} colSpan={6}>{search.trim() ? `Nothing matches "${search.trim()}".` : view === "upcoming" ? "No upcoming sessions." : view === "test" ? "No test data \u2014 clean." : "Nothing here."}</td></tr>}
              {bookings.map((b) => (
                <React.Fragment key={b.id}>
                <tr style={b.cancelled_at ? { opacity: .55 } : undefined}>
                  <td style={td}>
                    <span style={b.cancelled_at ? { textDecoration: "line-through" } : undefined}>{b.name}</span>
                    {b.note ? <div style={{ color: inkSoft, fontSize: ".8rem", marginTop: 3 }}>{b.note}</div> : null}
                    {b.admin_note ? (
                      <div style={{ fontSize: ".8rem", marginTop: 3, color: "#37506b",
                                    borderInlineStart: `2px solid ${clay}`, paddingInlineStart: 7 }}>
                        {b.admin_note}
                      </div>
                    ) : null}
                    {b.deleted_at ? (
                      <div style={{ fontSize: ".75rem", marginTop: 4, color: b.protected ? "#8a4b00" : inkSoft }}>
                        {b.protected
                          ? "kept \u2014 needs your confirmation to remove"
                          : `clears itself in ${b.days_left_in_bin} day${b.days_left_in_bin === 1 ? "" : "s"}`}
                      </div>
                    ) : null}
                  </td>
                  <td style={td}>{b.email}</td>
                  <td style={td}>
                    {b.plan_label}
                    {b.kind === "paid"
                      ? <div style={{ color: inkSoft, fontSize: ".8rem" }}>
                          {/* Charged and arrived, side by side. One figure was
                              all this row ever showed, which is why a payout
                              26 euros short of the sale went unnoticed. Nothing
                              is shown where nothing is known - an unrecorded
                              payout must not read as a payout of zero. */}
                          {money(b.amount, b.currency)}{b.amount_received != null ? " charged" : ""}
                          {b.amount_received != null
                            ? ` \u00b7 ${money(b.amount_received, b.settlement_currency || b.currency)} received`
                            : ""}
                          {b.offer_code ? ` \u00b7 code ${b.offer_code}` : ""}
                          {b.withheld_tax_cents
                            ? <div style={{ color: "#c0392b", fontSize: ".75rem", marginTop: 2 }}>
                                {money(b.withheld_tax_cents, b.settlement_currency || b.currency)} withheld as tax \u2014 you owe none
                              </div>
                            : null}
                        </div>
                      : <div style={{ color: inkSoft, fontSize: ".8rem" }}>Free</div>}
                  </td>
                  <td style={td}>{b.date}<div style={{ color: inkSoft, fontSize: ".8rem" }}>{b.slot}</div></td>
                  <td style={td}>
                    {b.cancelled_at
                      ? badge("cancelled", false)
                      : b.kind === "free" ? badge("free call", true) : badge(String(b.payment_status || "").replace(/_/g, " "), b.payment_status === "paid")}
                    {b.refund_id ? <div style={{ color: inkSoft, fontSize: ".75rem", marginTop: 3 }}>refunded {money(b.refund_amount, b.currency)}</div> : null}
                    {b.no_show_at ? <div style={{ marginTop: 4 }}>{badge("no-show", false)}</div> : null}
                    {b.is_test ? <div style={{ marginTop: 4 }}>{badge("test data", false)}</div> : null}
                    {b.archived_at ? <div style={{ color: inkSoft, fontSize: ".72rem", marginTop: 3 }}>archived</div> : null}
                  </td>
                  <td style={td}>
                    {/* In the bin the only questions are: bring it back, or end
                        it. Everything else is about a booking still in play. */}
                    {b.deleted_at ? (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button style={smallBtn} onClick={() => restore(b)}>Restore</button>
                        <button style={{ ...smallBtn, color: "#c0392b", borderColor: "rgba(192,57,43,.4)" }}
                                onClick={() => setConfirmPurge(b.id)}>Remove for good</button>
                        <button style={smallBtn}
                                onClick={() => { setEditingNote(b.id); setNoteDraft(b.admin_note || ""); }}>
                          {b.admin_note ? "Edit note" : "Add note"}
                        </button>
                      </div>
                    ) : b.cancelled_at ? (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ color: inkSoft, fontSize: ".8rem" }}>{b.cancel_reason || "\u2014"}</span>
                        <button style={{ ...smallBtn, color: "#c0392b", borderColor: "rgba(192,57,43,.4)" }}
                                onClick={() => setConfirmDelete(b.id)}>Delete</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button style={smallBtn} onClick={() => { setCancelling(b.id); setCancelReason(""); setCancelRefund(false); }}>Cancel</button>
                        <button style={smallBtn} onClick={() => getRescheduleLink(b.id)}>Reschedule link</button>
                        <button style={smallBtn} onClick={() => markNoShow(b)}>
                          {b.no_show_at ? "Clear no-show" : "No-show"}
                        </button>
                        <button style={smallBtn} onClick={() => toggleArchive(b)}>
                          {b.archived_at ? "Unarchive" : "Archive"}
                        </button>
                        <button style={smallBtn}
                                onClick={() => { setEditingNote(b.id); setNoteDraft(b.admin_note || ""); }}>
                          {b.admin_note ? "Edit note" : "Add note"}
                        </button>
                        {/* Marking as test is only offered where it is legal to
                            offer it. The server refuses it for anything paid, so
                            hiding the button here just avoids a pointless click. */}
                        {b.payment_status !== "paid" && (
                          <button style={smallBtn} onClick={() => toggleTest(b)}>
                            {b.is_test ? "Not test" : "Mark test"}
                          </button>
                        )}
                        {/* One delete button, on every row. It used to appear
                            only on test data, because it destroyed things. It
                            no longer destroys anything. */}
                        <button style={{ ...smallBtn, color: "#c0392b", borderColor: "rgba(192,57,43,.4)" }}
                                onClick={() => setConfirmDelete(b.id)}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>

                {cancelling === b.id && (
                  <tr>
                    <td style={{ ...td, background: "rgba(255,107,92,.06)" }} colSpan={6}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Cancel {b.name}&rsquo;s session?</div>
                      <div style={{ color: inkSoft, fontSize: ".84rem", marginBottom: 10 }}>
                        The booking is kept on record \u2014 it is an accounting record and cannot be deleted.
                        It will be marked cancelled and removed from your Calendly calendar.
                      </div>
                      <input
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Reason (the client sees this in Calendly)"
                        style={{ font: "inherit", fontSize: ".88rem", padding: "8px 11px", borderRadius: 9, border: `1px solid ${line}`, width: "min(420px, 100%)" }}
                      />
                      {b.kind === "paid" && b.payment_status === "paid" && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: ".88rem" }}>
                          <input type="checkbox" checked={cancelRefund} onChange={(e) => setCancelRefund(e.target.checked)} />
                          Also refund {money(b.amount, b.currency)} to the client
                        </label>
                      )}
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button
                          disabled={saving}
                          style={{ ...smallBtn, background: "#c0392b", color: "#fff", borderColor: "#c0392b" }}
                          onClick={() => cancelBooking(b.id)}
                        >{saving ? "Working\u2026" : "Yes, cancel it"}</button>
                        <button style={smallBtn} onClick={() => setCancelling(null)}>Keep it</button>
                      </div>
                    </td>
                  </tr>
                )}

                {confirmDelete === b.id && (
                  <tr>
                    <td style={{ ...td, background: "rgba(192,57,43,.07)" }} colSpan={6}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {b.protected ? "Money changed hands on this one" : "Move this to the bin?"}
                      </div>
                      <div style={{ color: inkSoft, fontSize: ".84rem", marginBottom: 10 }}>
                        {b.protected ? (
                          <>
                            {money(b.amount, b.currency)} was received
                            {b.payment_method ? ` (${b.payment_method})` : ""}. The record downloads to
                            this computer first \u2014 the server will not remove it otherwise. It then
                            goes to the bin and <b>stays there</b> until you remove it yourself; the
                            90-day timer never takes a paid booking.
                          </>
                        ) : (
                          <>It leaves every list and every total straight away, and sits in the bin
                          for 90 days in case you want it back.</>
                        )}
                      </div>
                      <input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
                             placeholder="Why (optional) \u2014 kept in the log, not on the booking"
                             style={{ font: "inherit", fontSize: ".86rem", padding: "8px 11px", borderRadius: 9,
                                      border: `1px solid ${line}`, width: "min(420px, 100%)", marginBottom: 10 }} />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...smallBtn, background: "#c0392b", color: "#fff", borderColor: "#c0392b" }}
                                onClick={() => del(b, deleteReason)}>
                          {b.protected ? "Download the record and bin it" : "Move to the bin"}
                        </button>
                        <button style={smallBtn} onClick={() => { setConfirmDelete(null); setDeleteReason(""); }}>Keep it</button>
                      </div>
                    </td>
                  </tr>
                )}

                {confirmPurge === b.id && (
                  <tr>
                    <td style={{ ...td, background: "rgba(192,57,43,.12)" }} colSpan={6}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Remove this for good?</div>
                      <div style={{ color: inkSoft, fontSize: ".84rem", marginBottom: 10 }}>
                        This is the step that cannot be undone. The booking is gone; the log entry
                        stays, so what was removed and why is still answerable
                        {b.protected ? " \u2014 and this one is an accounting record" : ""}.
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...smallBtn, background: "#c0392b", color: "#fff", borderColor: "#c0392b" }}
                                onClick={() => purge(b)}>Yes, remove it for good</button>
                        <button style={smallBtn} onClick={() => setConfirmPurge(null)}>Keep it in the bin</button>
                      </div>
                    </td>
                  </tr>
                )}

                {editingNote === b.id && (
                  <tr>
                    <td style={{ ...td, background: paper }} colSpan={6}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Your note</div>
                      <div style={{ color: inkSoft, fontSize: ".82rem", marginBottom: 8 }}>
                        Yours, not the client's \u2014 their own message is shown above and is never
                        touched by this. Keep it to facts. Record a payment in the payment fields,
                        never only here: a data request clears this text and cannot touch those.
                      </div>
                      <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={3}
                                style={{ font: "inherit", fontSize: ".88rem", padding: "8px 11px", borderRadius: 9,
                                         border: `1px solid ${line}`, width: "min(560px, 100%)" }} />
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <button style={smallBtn} onClick={() => saveNote(b, noteDraft)}>Save</button>
                        <button style={smallBtn} onClick={() => setEditingNote(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}

                {rowMsg[b.id] && (
                  <tr>
                    <td style={{ ...td, color: inkSoft, fontSize: ".84rem", background: "rgba(10,30,56,.03)" }} colSpan={6}>
                      {rowMsg[b.id]}
                      <button style={{ ...smallBtn, marginLeft: 10, padding: "2px 9px" }}
                              onClick={() => setRowMsg((m) => ({ ...m, [b.id]: "" }))}>dismiss</button>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {tab === "traffic" && (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ ...cardStyle }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
              <h2 style={{ ...serif, fontSize: "1.2rem" }}>Where people stop</h2>
              <div style={{ display: "flex", gap: 6 }}>
                {[7, 30, 90].map((d) => (
                  <button key={d} onClick={() => setTrafficDays(d)}
                    style={{ padding: "5px 12px", borderRadius: 100, font: "inherit", fontSize: ".8rem", cursor: "pointer",
                             fontWeight: 600, border: `1px solid ${trafficDays === d ? clay : line}`,
                             background: trafficDays === d ? clay : "#fff", color: trafficDays === d ? "#fff" : ink }}>
                    {d} days
                  </button>
                ))}
              </div>
            </div>
            <p style={{ color: inkSoft, fontSize: ".85rem", marginBottom: 16 }}>
              Each percentage is out of everyone who arrived &mdash; not out of the step above it &mdash;
              so a drop shows up at whichever step it actually happens.
            </p>

            {!traffic || !traffic.visits ? (
              <div style={{ color: inkSoft, fontSize: ".9rem" }}>
                No visits recorded yet in this window. Numbers start from the moment this went live,
                so there is nothing here from before that.
              </div>
            ) : traffic.funnel.map((f) => (
              <div key={f.step} style={{ marginBottom: 11 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".86rem", marginBottom: 4 }}>
                  <span>{f.step}</span>
                  <span style={{ color: inkSoft }}>
                    <b style={{ color: ink }}>{f.n}</b>{f.pct === null ? "" : ` \u00b7 ${f.pct}%`}
                  </span>
                </div>
                <div style={{ height: 8, borderRadius: 100, background: paper, overflow: "hidden" }}>
                  <div style={{ width: `${f.pct || 0}%`, height: "100%", background: clay, opacity: .85 }} />
                </div>
              </div>
            ))}

            {traffic && traffic.visits ? (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${line}`, color: inkSoft, fontSize: ".84rem" }}>
                Reduced-rate requests opened: <b style={{ color: ink }}>{traffic.extras.apply_open}</b>
                {" \u00b7 "}codes tried: <b style={{ color: ink }}>{traffic.extras.code_try}</b>
                {" \u00b7 "}waitlist joins: <b style={{ color: ink }}>{traffic.extras.waitlist}</b>
              </div>
            ) : null}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 }}>
            <Breakdown title="Language" rows={traffic ? traffic.languages : []} />
            <Breakdown title="Came from" rows={traffic ? traffic.referrers : []} />
            <Breakdown title="Device" rows={traffic ? traffic.devices : []} />
            <Breakdown title="Plans looked at" rows={traffic ? traffic.plans : []} />
            <Breakdown
              title="Country"
              rows={traffic ? traffic.countries : []}
              empty={"Your host does not tell me the visitor's country, so this stays empty. " +
                     "Putting the site behind Cloudflare (free) would switch it on \u2014 no code change needed."} />
          </div>
        </div>
      )}

      {tab === "waitlist" && (
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <table data-testid="waitlist-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Name</th><th style={th}>Email</th><th style={th}>Interested in</th></tr></thead>
            <tbody>
              {waitlist.length === 0 && <tr><td style={td} colSpan={3}>No one on the waitlist yet.</td></tr>}
              {waitlist.map((w) => (<tr key={w.id}><td style={td}>{w.name}</td><td style={td}>{w.email}</td><td style={td}>{w.plan || "\u2014"}</td></tr>))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "data" && (
        <DataRequestPanel requests={dataRequests} onLogged={load} />
      )}

      {tab === "applications" && (
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <table data-testid="applications-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Who</th><th style={th}>What they wrote</th><th style={th}>Status</th><th style={th}></th></tr></thead>
            <tbody>
              {applications.length === 0 && <tr><td style={td} colSpan={4}>Nobody has asked for a reduced place yet.</td></tr>}
              {applications.map((a) => (
                <tr key={a.id}>
                  <td style={td}>
                    {a.name}
                    <div style={{ color: inkSoft, fontSize: ".8rem" }}>{a.email}</div>
                    <div style={{ color: inkSoft, fontSize: ".75rem", marginTop: 3 }}>{new Date(a.created_at).toLocaleDateString()}</div>
                  </td>
                  <td style={{ ...td, maxWidth: 420, whiteSpace: "pre-wrap" }}>{a.message || "\u2014"}</td>
                  <td style={td}>{badge(a.status, a.status === "offered")}</td>
                  <td style={td}>
                    <select value={a.status} onChange={(e) => setApplicationStatus(a.id, e.target.value)}
                      style={{ font: "inherit", fontSize: ".85rem", padding: "6px 8px", borderRadius: 8, border: `1px solid ${line}` }}>
                      <option value="new">new</option>
                      <option value="offered">offered</option>
                      <option value="declined">declined</option>
                      <option value="closed">closed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "offers" && (
        <>
          <OfferForm catalog={catalog} onCreated={load} />
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <table data-testid="offers-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Code</th><th style={th}>For</th><th style={th}>Price</th><th style={th}>Who</th><th style={th}>State</th><th style={th}></th></tr></thead>
              <tbody>
                {offers.length === 0 && <tr><td style={td} colSpan={6}>No offers issued yet.</td></tr>}
                {offers.map((o) => (
                  <tr key={o.id}>
                    <td style={{ ...td, fontFamily: "ui-monospace, monospace", letterSpacing: ".08em" }}>
                      {o.code}
                      {o.note ? <div style={{ color: inkSoft, fontSize: ".8rem", fontFamily: "inherit", letterSpacing: 0, marginTop: 3 }}>{o.note}</div> : null}
                    </td>
                    <td style={td}>{o.plan_label}</td>
                    <td style={td}>{money(o.amount, o.currency)}</td>
                    <td style={td}>{o.used_by_email || o.email || "anyone with the code"}</td>
                    <td style={td}>{badge(o.state, o.state === "open" || o.state === "used")}
                      <div style={{ color: inkSoft, fontSize: ".75rem", marginTop: 4 }}>
                        {o.state === "open" ? `expires ${new Date(o.expires_at).toLocaleDateString()}` : ""}
                      </div>
                    </td>
                    <td style={td}>
                      {o.state === "open" && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button onClick={() => copyLink(o)} style={smallBtn}>Copy link</button>
                          <button onClick={() => revokeOffer(o.id)} style={{ ...smallBtn, color: "#c0392b", borderColor: "#e2b3ad" }}>Revoke</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const t = localStorage.getItem(TKEY);
      if (!t) { setAuthed(false); return; }
      const res = await fetch(`${API}/admin/me`, { headers: authHeaders() });
      setAuthed(res.ok);
      if (!res.ok) localStorage.removeItem(TKEY);
    })();
  }, []);

  const logout = () => { localStorage.removeItem(TKEY); setAuthed(false); };

  if (authed === null) return <div style={{ ...shell, display: "grid", placeItems: "center" }}><div style={{ marginTop: 120, color: inkSoft }}>Loading…</div></div>;
  if (!authed) return <Login onDone={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
